/**
 * *Sledovať* / *Sledované* on an entity (PRD-0075 R-B1): watching puts it on the case's default
 * watchlist, whose occurrence rule alerts when a new source mentions it. Pressed again it stops.
 *
 * Presentational: the feature (`features/monitor/WatchToggle`) knows whether the entity is watched
 * and whether the reader may change it; a viewer gets `onToggle: null` and sees a read-only badge.
 */
import { Eye, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

const WATCH = 'cases.monitor.watch'

export interface WatchToggleButtonProps {
  watched: boolean
  pending: boolean
  /** Null for a reader who may not change what is watched. */
  onToggle: (() => void) | null
}

export function WatchToggleButton({ watched, pending, onToggle }: WatchToggleButtonProps) {
  const { t } = useTranslation()
  if (!onToggle) {
    return watched ? (
      <Badge variant="info">
        <Eye aria-hidden />
        {t(`${WATCH}.watched`)}
      </Badge>
    ) : null
  }
  const Icon = pending ? Loader2 : Eye
  return (
    <Button
      type="button"
      variant={watched ? 'secondary' : 'outline'}
      size="xs"
      aria-pressed={watched}
      disabled={pending}
      className="w-fit"
      title={t(watched ? `${WATCH}.unwatchHint` : `${WATCH}.watchHint`)}
      onClick={onToggle}
    >
      <Icon aria-hidden className={pending ? 'animate-spin' : undefined} />
      {t(watched ? `${WATCH}.watched` : `${WATCH}.watch`)}
    </Button>
  )
}
